import React, { useState } from 'react';
import { Layout, Menu, theme, Avatar, Dropdown, Space } from 'antd';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  DashboardOutlined,
  UserOutlined,
  SettingOutlined,
  BarChartOutlined,
  LogoutOutlined,
  TagsOutlined,
  BellOutlined,
  AppstoreOutlined,
} from '@ant-design/icons';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTokenRefresh } from '../hooks/useTokenRefresh';
import { config, isProduction } from '../config';
import EnvironmentInfo from '../components/EnvironmentInfo';

const { Header, Sider, Content } = Layout;

const MainLayout: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  
  // 启用令牌自动刷新（已支持非JWT格式的token）
  useTokenRefresh();
  
  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const menuItems = [
    {
      key: '/dashboard',
      icon: <DashboardOutlined />,
      label: '首页',
    },
    // {
    //   key: 'traffic',
    //   icon: <BarChartOutlined />,
    //   label: '流量管理',
    //   children: [
    //     {
    //       key: '/traffic/import',
    //       label: '流量卡导入',
    //     },
    //     {
    //       key: '/traffic/list',
    //       label: '流量卡列表',
    //     },
    //   ],
    // },
    {
      key: 'device',
      icon: <SettingOutlined />,
      label: '设备管理',
      children: [
        {
          key: '/device/type',
          label: '设备类型',
        },
        {
          key: '/device/batch',
          label: '设备批次',
        },
        {
          key: '/device/list',
          label: '设备列表',
        },
        // {
        //   key: '/device/import',
        //   label: '设备导入',
        // },
      ],
    },
    {
      key: 'package',
      icon: <TagsOutlined />,
      label: '套餐管理',
      children: [
        // {
        //   key: '/package/template',
        //   label: '套餐模板',
        // },
        {
          key: '/package/group/list',
          label: '套餐组管理',
        },
        {
          key: '/package/list',
          label: '套餐列表',
        },
      ],
    },
    {
      key: 'order',
      icon: <BarChartOutlined />,
      label: '订单管理',
      children: [
        {
          key: '/order/statistics',
          label: '订单统计',
        },
        {
          key: '/order/list',
          label: '订单列表',
        },

      ],
    },
    {
      key: 'config',
      icon: <AppstoreOutlined />,
      label: '配置管理',
      children: [
        {
          key: '/config/payment-app-credential',
          label: '应用资质配置',
        },
        {
          key: '/config/payment-merchant',
          label: '商户/收款账号',
        },
        {
          key: '/config/payment',
          label: '平台支付路由',
        },
        {
          type: 'group' as const,
          label: '老通道',
          children: [
            {
              key: '/config/wx-app',
              label: '小程序列表',
            },
            {
              key: '/config/merchant',
              label: '商户号列表',
            },
            // {
            //   key: '/config/payment-legacy',
            //   label: '支付通道配置',
            // },
          ],
        },
      ],
    },
    // {
    //   key: '/merchant',
    //   icon: <UserOutlined />,
    //   label: '商户号管理',
    // },
    // {
    //   key: '/finance',
    //   icon: <BarChartOutlined />,
    //   label: '财务管理',
    // },
    // {
    //   key: 'dealer',
    //   icon: <UserOutlined />,
    //   label: '经销商管理',
    //   children: [
    //     {
    //       key: '/dealer/list',
    //       label: '经销商列表',
    //     },
    //   ],
    // },
    // {
    //   key: 'user',
    //   icon: <UserOutlined />,
    //   label: '用户管理',
    //   children: [
    //     {
    //       key: '/user/list',
    //       label: '用户列表',
    //     },
    //     {
    //       key: '/user/role',
    //       label: '角色管理',
    //     },
    //   ],
    // },
  ];

  const userMenuItems = [
    // {
    //   key: 'profile',
    //   icon: <UserOutlined />,
    //   label: '个人资料',
    // },
    // {
    //   key: 'settings',
    //   icon: <SettingOutlined />,
    //   label: '设置',
    // },
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
    },
  ];

  const handleMenuClick = ({ key }: { key: string }) => {
    navigate(key);
  };

  const handleUserMenuClick = ({ key }: { key: string }) => {
    if (key === 'logout') {
      logout();
      navigate('/login');
    }
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider trigger={null} collapsible collapsed={collapsed}>
        <div style={{ 
          height: 32, 
          margin: 16, 
          background: 'rgba(255, 255, 255, 0.2)',
          borderRadius: 6,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontSize: collapsed ? 12 : 16,
          fontWeight: 'bold'
        }}>
          {collapsed ? 'MP' : config.appName}
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[location.pathname]}
          items={menuItems}
          onClick={handleMenuClick}
        />
      </Sider>
      <Layout>
        <Header style={{ 
          padding: '0 16px', 
          background: colorBgContainer,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {React.createElement(collapsed ? MenuUnfoldOutlined : MenuFoldOutlined, {
              className: 'trigger',
              onClick: () => setCollapsed(!collapsed),
              style: { fontSize: 18, cursor: 'pointer' }
            })}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            {!isProduction && (
              <EnvironmentInfo showVersion={false} showDetails={false} />
            )}
            <BellOutlined style={{ fontSize: 18, cursor: 'pointer' }} />
            <Dropdown
              menu={{
                items: userMenuItems,
                onClick: handleUserMenuClick,
              }}
              placement="bottomRight"
            >
              <Space style={{ cursor: 'pointer' }}>
                <Avatar icon={<UserOutlined />} src={user?.avatar} />
                <span>{user?.realName || '管理员'}</span>
              </Space>
            </Dropdown>
          </div>
        </Header>
        <Content
          style={{
            margin: '24px 16px',
            padding: 24,
            minHeight: 280,
            background: colorBgContainer,
            borderRadius: borderRadiusLG,
          }}
        >
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
};

export default MainLayout; 